import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { DocSizeError } from '@common/html/constants/doc-size-error.constant';
import { TrackedChangesIgnoreMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { createTransactionForState } from '@common/prosemirror/recreate-transform/recreate';
import { recreateTransformWithJSONPatch } from '@common/prosemirror/recreate-transform/recreate-transform-with-json-patch';
import { CollabSessionOptions } from '@common/sockets/collab-session';
import { CodeEditorComponent } from '@portal-core/code-editor/components/code-editor/code-editor.component';
import { CodeEditorService } from '@portal-core/code-editor/services/code-editor.service';
import { FileSizeService } from '@portal-core/general/services/file-size.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareTextEditorComponent } from '@portal-core/project-files/components/flare-text-editor/flare-text-editor.component';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { FlareTextEditorType } from '@portal-core/project-files/types/flare-text-editor-type.type';
import { FlareXmlEditorMode } from '@portal-core/project-files/types/flare-xml-editor-mode.type';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { debounce } from 'lodash';

@Component({
  selector: 'mc-flare-xml-editor',
  templateUrl: './flare-xml-editor.component.html',
  styleUrls: ['./flare-xml-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlareXmlEditorComponent implements OnChanges {
  @ViewChild(FlareTextEditorComponent, { static: true }) flareTextEditor: FlareTextEditorComponent;
  @ViewChild(CodeEditorComponent, { static: true }) codeEditor: CodeEditorComponent;

  @Input() branchName: string;
  @Input() commitId: string;
  @Input() content?: string;
  @Input() editorType?: FlareTextEditorType = 'solo';
  @Input() fileId: number;
  @Input() filePath: string;
  @Input() fileType: CollabFileType;
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() licenseUser: LicenseUser;
  @Input() mode?: FlareXmlEditorMode = 'text';
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly?: boolean = false;
  @Input() showConditions?: boolean;
  @Input() stylesEnabled?: boolean;
  @Input() trackedChanges?: boolean = false;
  @Input() timelineEnabled?: boolean = false;
  @Input() usingWorkspaceTree?: boolean = false;
  @Input() wrapLines?: boolean;

  @Output() contentChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() load: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() modeChange: EventEmitter<FlareXmlEditorMode> = new EventEmitter<FlareXmlEditorMode>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() trackedChangesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  ProjectFileType: typeof ProjectFileType = ProjectFileType;

  /** The content used as input to the code editor. */
  codeContent: string;
  /** Whether or not the doc exceeds the max size. */
  docExceedsMaxSize: boolean;
  /** The size of the doc when it exceeds the max size. */
  docSize: number;
  /** The content used as input to the text editor. */
  textContent: string;
  /** Whether or not the content was able to be initially loaded into the text editor in ngOnChanges. */
  private textEditorMissingContent: boolean;
  /** The text content serialized in the last text validation. */
  private validatedTextContent: string;

  private debouncedTextValidate: FlareXmlEditorComponent['validateText'] = debounce(() => {
    this.validateText();
  }, 1000);

  private debouncedValidate: FlareXmlEditorComponent['validate'] = debounce((syncCodeEditorToTextEditor?: boolean) => {
    this.validate(syncCodeEditorToTextEditor);
    this.cdr.markForCheck();
  }, 1000);

  public get docId(): string {
    return this.flareTextEditor?.docId;
  }

  public get docVersion(): number {
    return this.flareTextEditor?.docVersion;
  }

  /** Returns whether the text content is valid. */
  public get textValid(): boolean {
    return this._textValid;
  }
  private _textValid: boolean = true;

  /** Returns Whether the text content has been validated since it last changed. */
  public get textValidated(): boolean {
    return this._textValidated;
  }
  private _textValidated: boolean;

  /** Returns whether the content is valid Flare xml. */
  public get valid(): boolean {
    return this._valid;
  }
  private _valid: boolean = true;

  /** Returns whether the content has been validated since it last changed. */
  public get validated(): boolean {
    return this._validated;
  }
  private _validated: boolean;

  /** Returns the validation error from the most recent validation. */
  public get validationError(): Error {
    return this._validationError;
  }
  private _validationError: Error;

  /** Returns the display text the code editor's status bar. */
  get codeEditorErrorText(): string {
    // Use a custom message for the doc size error
    if (this.validationError?.name === DocSizeError) {
      return `Document is too large to load in Content tab. Current size: ${this.fileSizeService.format((this.validationError as any).docSize, 2)}. Max size: ${this.fileSizeService.format(this.textEditorService.flareTextEditorDocMaxCharLength, 0)}.`;
    } else {
      return this.validationError?.toString();
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private codeEditorService: CodeEditorService,
    private fileSizeService: FileSizeService,
    private textEditorService: TextEditorService,
    private projectFilesService: ProjectFilesService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    // If the content changed then the code content needs to be reset
    if (this.editorType === 'solo' && changes.content) {
      // Reset the validation state now that there is new content
      this._validationError = null;
      this._valid = true;
      this._validated = false;
      this.docExceedsMaxSize = false;
      this.docSize = null;
      this._textValid = true;
      this._textValidated = false;
      this.validatedTextContent = null;

      // Check file type to detect false positives when switching the editor from other file types.
      // If we didn't do this there would likely be a force switch to code editor because the other file type content would not be able to pass validation.
      if (this.content && this.projectFilesService.getFileType(this.filePath) === ProjectFileType.Flare) {
        // Validate the content
        const validationError = this.validateCode(this.content);

        // If the content is valid
        if (!validationError) {
          // Serialize to the ProseMirror doc and back to code. This ensures we are initializing the code content to exactly what we'd get from the text editor
          // If we didn't do this there would likely be a doc change on the first switch to the code editor because the formatting or attribute order would change
          const doc = this.flareTextEditor.schema.codeToDoc(this.content);
          this.codeContent = this.formatCode(this.flareTextEditor.schema.nodeToCode(doc));
          this.textContent = this.content;
        } else {
          // Since the code is invalid we can't serialize it with ProseMirror. Just set the code content equal to the content so that the user can edit it
          this.codeContent = this.content;
          this.textContent = null;
          this.textEditorMissingContent = true; // This must be set for the mode to properly switch to the code editor
          // Make sure the mode is set to code
          if (this.mode !== 'code') {
            this.mode = 'code';
            this.modeChange.emit(this.mode);
          }
        }
      } else {
        this.codeContent = null;
        this.textContent = null;
      }

      // If the code editor is active then validate the new code content
      if (this.mode === 'code') {
        this.debouncedValidate();
      }
    }

    // If the content did not change but the mode changed
    if (!changes.content && changes.mode) {
      // If switching to the code editor then the content needs to be copied over from the text editor
      if (this.mode === 'code') {
        // If the code is invalid then we can't switch to the code editor
        if (this.canChangeFromModeToMode('text', 'code')) {
          // If the content could not be loaded into the text editor initially then the mode is automatically set to code which means this code will run
          // We don't want to update the code editor with nothing from the text editor so leave the code editor content alone
          if (!this.textEditorMissingContent) {
            this.syncTextEditorToCodeEditor();
          }
        }
      }
      // Else if switching to the text editor then the code content needs to be validated and applied to the text editor
      else if (this.mode === 'text' || this.mode === 'props') {
        // If the code is invalid then we can't switch to the text editor
        if (this.canChangeFromModeToMode('code', 'text')) {
          this.syncCodeEditorToTextEditor();
        }
      }
    }

    // If the mode changed to the content tab then reflow the toolbar layout to account for viewport resizing while on the code tab
    if (changes.mode) {
      if (this.mode === 'text') {
        this.flareTextEditor.reflowToolbarLayout();
      }
    }
  }

  onCollabSessionLoaded(success: boolean) {
    // After the collab session loads sync its content into the code editor
    if (success && this.editorType === 'collab') {
      this.validateText();
      this.syncTextEditorToCodeEditor();
    }

    this.load.emit(success);
  }

  onCodeContentChanged() {
    this._validated = false;
    this.contentChange.emit();
    this.debouncedValidate(true);
  }

  onTextContentChanged(event: EditorChangeEvent) {
    this._textValidated = false;
    this.contentChange.emit(event);

    // If the text isn't valid then check to see if the change made it valid. This is only being done when the text isn't valid because it is expensive to validate the text
    if (!this.textValid) {
      this.debouncedTextValidate();
    }
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }

  onStylesEnabledChanged(stylesEnabled: boolean) {
    this.stylesEnabled = stylesEnabled;
    this.stylesEnabledChange.emit(stylesEnabled);
  }

  onTrackedChangesChanged(trackedChanges: boolean) {
    this.trackedChanges = trackedChanges;
    this.trackedChangesChange.emit(trackedChanges);
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLines = wrapLines;
    this.wrapLinesChange.emit(wrapLines);
  }

  public canChangeMode(mode: FlareXmlEditorMode): boolean {
    return this.canChangeFromModeToMode(this.mode, mode);
  }

  public getContent(): string {
    if (this.mode === 'code') {
      return this.codeEditor?.getContent();
    } else {
      // First validate the text content to see if it can even be serialized
      this.validateText();
      return this.textValid ? this.validatedTextContent : undefined;
    }
  }

  public getFormattedContent(): string {
    const content = this.getContent();
    if (typeof content === 'string') {
      return this.formatCode(content);
    }
  }

  public validate(syncCodeEditorToTextEditor: boolean = false) {
    // Validating is expensive so only validate if its needed
    if (!this.validated) {
      this._validationError = this.validateCode(this.codeEditor.getContent());
      this._valid = !this._validationError;
      this._validated = true;
      if (syncCodeEditorToTextEditor && this.editorType === 'collab') {
        this.syncCodeEditorToTextEditor();
      }
    }
  }

  private validateText() {
    if (!this.textValidated) {
      try {
        this.validatedTextContent = this.flareTextEditor.getContent();
        this._textValid = true;
        this.docExceedsMaxSize = false;
        this.docSize = null;
      } catch (error) {
        this.validatedTextContent = null;
        this._textValid = false;
        this.docExceedsMaxSize = error.name === DocSizeError;
        this.docSize = error.docSize;
      }

      this._textValidated = true;
      this.cdr.markForCheck();
    }
  }

  private validateCode(code: string): Error {
    let validationError = this.flareTextEditor.schema.validate(code, { validateXmlDeclaration: true });

    if (!validationError) {
      try {
        // Serialize to the ProseMirror doc and back to code. This ensures we are fully validating the code
        const doc = this.flareTextEditor.schema.codeToDoc(code);
        this.flareTextEditor.schema.nodeToCode(doc);
      } catch (error) {
        validationError = error;
      }
    }

    return validationError;
  }

  private canChangeFromModeToMode(fromMode: FlareXmlEditorMode, toMode: FlareXmlEditorMode): boolean {
    let newMode: FlareXmlEditorMode;

    // If moving from the code editor to the text editor
    if (fromMode === 'code' && (toMode === 'text' || toMode === 'props')) {
      // Make sure the code content has been validated
      this.validate();

      // Only change to the text editor if the code content is valid
      if (this.valid) {
        newMode = toMode;
      }
    }
    // Else if moving from the text editor to the code editor
    else if ((fromMode === 'text' || fromMode === 'props') && toMode === 'code') {
      // Make sure the text content can be serialized to code
      this.validateText();

      if (this.textValid) {
        newMode = toMode;
      }
    } else {
      newMode = toMode;
    }

    return newMode === toMode;
  }

  private formatCode(code: string): string {
    return this.codeEditorService.formatByFileType(code, ProjectFileType.Flare);
  }

  private syncTextEditorToCodeEditor() {
    // Create a transaction that will replace the code editor content with the text content
    const tr = this.codeEditor.editorState.update({
      changes: {
        from: 0,
        to: this.codeEditor.editorState.doc.length,
        insert: this.formatCode(this.validatedTextContent) // Use this.validatedTextContent because canChangeFromModeToMode set it
      }
    });

    // Only apply the transaction if the doc changed
    if (tr.docChanged && tr.newDoc.toString() !== this.codeEditor.getContent()) {
      this.codeEditor.editorView.dispatch(tr);
    }

    // Validate the latest code content
    this.debouncedValidate();
  }

  private syncCodeEditorToTextEditor() {
    // Create a transaction that will update the text editor content with the least amount of steps
    const doc = this.flareTextEditor.schema.codeToDoc(this.codeEditor.getContent());
    const transform = recreateTransformWithJSONPatch(this.flareTextEditor.editorState.doc, doc);

    // The text editor needs to update its gutter while the editor is visible. Because mode just changed the element is not yet visible. Delay setting the state and reflowing so that the editor is visible when it reflows its gutter.
    setTimeout(() => {
      // Only apply the transaction if the docs are different
      if (transform.docChanged) {
        const tr = createTransactionForState(this.flareTextEditor.editorState, transform.steps);
        tr.setMeta(TrackedChangesIgnoreMetaKey, true);

        this.flareTextEditor.editorView.dispatch(tr);
        this.textEditorMissingContent = false; // Set this to false now that content has been loaded into the text editor
      }

      // The gutter needs to reflow even if the doc didn't change. This is especially true if the doc loaded with the code editor visible. Because the gutter was not yet visible it hasn't properly laid out its items yet.
      // Don't force the reflow because the items might already be laid out correctly and there is no reason to reflow if thats the case.
      this.flareTextEditor.reflowGutterLayout(false);
    });

    // Always set the serialization state because the state can change even if the ProseMirror doc didn't change
    // eg deleting the xml declaration in the code and then switching to the text editor and back to the code editor. The xml declaration is not a part of the ProseMirror doc but is tracked in the serialization state
    this.flareTextEditor.doc = doc;
  }
}
